<template>
    <div class="grafico-container">
        <div class="grafico-header">
            <h3 class="grafico-titulo">Status de Prazo dos Projetos</h3>
            <GraficoInfoIcon 
                titulo="Status de Prazo dos Projetos"
                descricao="Mostra o status dos projetos em relação ao prazo previsto (dtTermino). Verde: dentro do prazo, Amarelo: próximo do prazo, Vermelho: atrasado."
            />
        </div>
        <div v-if="!temDados" class="sem-dados">Nenhum dado disponível</div>
        <Bar v-else :data="chartData" :options="chartOptions" />
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import GraficoInfoIcon from './GraficoInfoIcon.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
    name: 'GraficoStatusPrazoProjetos',
    components: { Bar, GraficoInfoIcon },
    props: {
        projetos: {
            type: Array,
            default: () => []
        }
    },
    emits: ['clicar-projeto'],
    methods: {
        formatarNomeProjeto(projeto, maxLength = 25) {
            const nome = projeto.nome || 'Sem nome';
            const codigoPCM = projeto.pcm?.codigo || '';
            const nomeCompleto = codigoPCM ? `${codigoPCM} - ${nome}` : nome;
            return nomeCompleto.length > maxLength ? nomeCompleto.substring(0, maxLength) + '...' : nomeCompleto;
        },
        calcularStatusPrazo(projeto) {
            // Requer PCM com dtEstimadaFinal
            if (!projeto.pcm || !projeto.pcm.dtEstimadaFinal || projeto.encerrado) return null;
            
            const dtEstimadaFinal = projeto.pcm.dtEstimadaFinal;
            
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            const termino = new Date(dtEstimadaFinal);
            termino.setHours(0, 0, 0, 0);
            
            const diasRestantes = Math.ceil((termino - hoje) / (1000 * 60 * 60 * 24));
            
            if (diasRestantes < 0) {
                // Projeto atrasado - retornar valor negativo para visualização
                return { status: 'Atrasado', dias: diasRestantes, cor: 'rgba(231, 76, 60, 0.6)', borda: 'rgba(231, 76, 60, 1)' };
            } else if (diasRestantes <= 7) {
                return { status: 'Urgente', dias: diasRestantes, cor: 'rgba(241, 196, 15, 0.6)', borda: 'rgba(241, 196, 15, 1)' };
            } else if (diasRestantes <= 30) {
                return { status: 'Próximo', dias: diasRestantes, cor: 'rgba(52, 152, 219, 0.6)', borda: 'rgba(52, 152, 219, 1)' };
            } else {
                return { status: 'No Prazo', dias: diasRestantes, cor: 'rgba(46, 204, 113, 0.6)', borda: 'rgba(46, 204, 113, 1)' };
            }
        }
    },
    computed: {
        projetosComPrazo() {
            return this.projetos
                .map(p => ({
                    projeto: p,
                    statusPrazo: this.calcularStatusPrazo(p)
                }))
                .filter(item => item.statusPrazo !== null)
                .sort((a, b) => {
                    // Ordenar: atrasados primeiro (mais atrasados primeiro), depois por dias restantes (menos dias primeiro)
                    if (a.statusPrazo.status === 'Atrasado' && b.statusPrazo.status !== 'Atrasado') return -1;
                    if (a.statusPrazo.status !== 'Atrasado' && b.statusPrazo.status === 'Atrasado') return 1;
                    // Para atrasados, ordenar do mais atrasado para o menos atrasado (valores mais negativos primeiro)
                    if (a.statusPrazo.status === 'Atrasado' && b.statusPrazo.status === 'Atrasado') {
                        return a.statusPrazo.dias - b.statusPrazo.dias; // Mais negativo primeiro
                    }
                    // Para os outros, ordenar do menos dias para mais dias
                    return a.statusPrazo.dias - b.statusPrazo.dias;
                })
                .slice(0, 30);
        },
        temDados() {
            return this.projetosComPrazo.length > 0;
        },
        chartData() {
            const projetos = this.projetosComPrazo;
            // Converter valores negativos para positivos para exibição da esquerda para direita
            return {
                labels: projetos.map(item => this.formatarNomeProjeto(item.projeto)),
                datasets: [{
                    label: 'Dias',
                    data: projetos.map(item => Math.abs(item.statusPrazo.dias)),
                    backgroundColor: projetos.map(item => item.statusPrazo.cor),
                    borderColor: projetos.map(item => item.statusPrazo.borda),
                    borderWidth: 2
                }]
            };
        },
        chartOptions() {
            const self = this;
            return {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y',
                onClick: (event, elements) => {
                    if (elements.length > 0) {
                        const index = elements[0].index;
                        const item = self.projetosComPrazo[index];
                        if (item) {
                            self.$emit('clicar-projeto', item.projeto.id);
                        }
                    }
                },
                scales: {
                    x: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Dias'
                        },
                        ticks: {
                            callback: (value) => {
                                return `${value}d`;
                            }
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Projetos'
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const item = self.projetosComPrazo[context.dataIndex];
                                if (!item || !item.statusPrazo) return '';
                                
                                const status = item.statusPrazo.status;
                                const dias = item.statusPrazo.dias;
                                const diasAbsoluto = Math.abs(dias);
                                // Usar apenas dtEstimadaFinal do PCM (obrigatório para este gráfico)
                                const dtEstimadaFinal = item.projeto.pcm?.dtEstimadaFinal;
                                const dtTermino = dtEstimadaFinal ? new Date(dtEstimadaFinal).toLocaleDateString('pt-BR') : 'Não informado';
                                
                                if (status === 'Atrasado') {
                                    return [
                                        `Status: ${status}`,
                                        `Atrasado: ${diasAbsoluto} dia(s)`,
                                        `Término Previsto: ${dtTermino}`
                                    ];
                                }
                                return [
                                    `Status: ${status}`,
                                    `Dias Restantes: ${diasAbsoluto}`,
                                    `Término Previsto: ${dtTermino}`
                                ];
                            },
                            footer: () => 'Clique para ver detalhes'
                        }
                    }
                }
            };
        }
    }
};
</script>

<style scoped>
.grafico-container {
    background: var(--cor-bg, white);
    border: 1px solid var(--cor-separador, #ddd);
    border-radius: 8px;
    padding: 1.5rem;
    height: 600px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}

.grafico-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.grafico-titulo {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--cor-fonte, #333);
}

.sem-dados {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
}
</style>

